import { Arrays, DatelyObject, Dates, Numeric } from "../../util";
import { yearlyTaxFreeYears } from "../../codegen";
import { InputEnumOption } from "../../ui/InputEnumOption";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { UsecaseRow } from "../UsecaseRow";
import { CalculationRowType } from "../../model";
import type { MilageOwnCarUsecase } from "../MilageOwnCarUsecase";
import { Translations } from "../../logic";

/**
 * Usecase logic for Milage (km) using own car.
 */
export class MilageOwnCarUsecaseLogic extends BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   */
  public updateUsecase(row: UsecaseRow<CalculationRowType.MilageOwnCar, MilageOwnCarUsecase>, date: DatelyObject): void {
    const toEur = (val: number) => Numeric.round(val / 100);
    const m = this.getMilage(date);

    row.data.additionsAllowed = false;
    row.data.passengersAllowed = false;

    if (row.data.vehicleType == "custom") {
      // Price is set manually.
    } else if (row.data.vehicleType == "carLimitedBenefit") {
      row.price = toEur(m.carLimitedBenefit);
    } else {
      const price = m.other[row.data.vehicleType || "nothing"];
      if (price) {
        row.price = toEur(price);
        row.data.passengersAllowed = true;
      } else if(row.data.vehicleType === "car" || row.price === toEur(m.car) || !row.price) {
        // Set as default => handle below.
        row.data.vehicleType = "car";
        row.price = toEur(m.car);
        row.data.additionsAllowed = true;
        row.data.passengersAllowed = true;
      } else {
        row.data.vehicleType = "custom";
      }
    }

    if (row.data.passengersAllowed) {
      row.price ??= 0;
      row.price += toEur(((row.data.passengerCount || 0) * m.passanger)) ?? 0;
    }

    if (row.data.additionsAllowed) {
      Arrays.assureArray(row.data.additions || []).forEach((x) => {
        row.price ??= 0;
        row.price += toEur(m.carAdditions[x] || 0) ?? 0;
      });
    }
    row.price = Numeric.round(row.price);
  }

  /** Gets validation message for BoardUsecaseLogic  */
  public getUcValidation(): UsecaseValidationMessage | undefined {
    return undefined;
  }

  /**
   * Gets the vehicle type selection.
   * @param date Date for which the selection is fetched (default: today). The prices changes yearly an also the selection may change.
   */
  public getVehicleTypeSelection(date: DatelyObject = "today"): InputEnumOption[] {
    const m = this.getMilage(date);
    const toEur = (val: number) => Numeric.round(val / 100);
    const result: InputEnumOption[] = [{
      value: "car",
      text: `${Translations.get("SALAXY.CORE.yearlyTaxfreeYears.milage.car")} (${toEur(m.car)}€)`,
    }, {
      value: "carLimitedBenefit",
      text: `${Translations.get("SALAXY.CORE.yearlyTaxfreeYears.milage.carLimitedBenefit")} (${toEur(m.carLimitedBenefit)}€)`,
    }];
    Object.entries(m.other).forEach((x) =>
      result.push({
        value: x[0],
        text: `${Translations.get(`SALAXY.CORE.yearlyTaxfreeYears.milage.other.${x[0]}`)} (${toEur(x[1])}€)`,
      })
    );
    result.push({
      value: "custom",
      text: `${Translations.get("SALAXY.CORE.yearlyTaxfreeYears.milage.custom")}`,
    });
    return result;
  }

  /**
   * Gets the vehicle type selection.
   * @param date Date for which the selection is fetched (default: today). The prices changes yearly an also the selection may change.
   */
  public getAdditionsSelection(date: DatelyObject = "today"): InputEnumOption[] {
    const m = this.getMilage(date);
    const toEur = (val: number) => Numeric.round(val / 100);
    return Object.entries(m.carAdditions).map((x) => ({
        value: x[0],
        text: `${Translations.get(`SALAXY.CORE.yearlyTaxfreeYears.milage.carAdditions.${x[0]}`)} (${toEur(x[1])}€)`,
      }));
  }

  private getMilage(date: DatelyObject) {
    const year = Dates.getYear(date);
    const m = yearlyTaxFreeYears.find((x) => x.year == year)?.milage;
    if (!m) {
      throw new Error(`No yearlyTaxFreeYears year for date ${date}`);
    }
    return m;
  }
}
